import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { RichTextComponent } from '@shared/components/rich-text/rich-text.component';
import { richTextToPlain } from '@shared/utils/html-sanitizer';
import { ISSUE_MODULES, IssueContext, IssueDraft, IssueModule, IssueReportService } from './issue-report.service';

const FILE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff',
  'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain', 'text/csv']);

@Component({
  selector: 'qms-issue-report-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule,
    MatSelectModule, MatIconModule, RichTextComponent],
  template: `
    <h2 mat-dialog-title>Report issue</h2>
    <mat-dialog-content>
      <form [formGroup]="form" class="report-form" (ngSubmit)="submit()" id="issue-report-form">
        <mat-form-field appearance="outline">
          <mat-label>Title</mat-label>
          <input matInput formControlName="title" maxlength="200" required />
          <mat-error>Enter a title, up to 200 characters.</mat-error>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Module</mat-label>
          <mat-select formControlName="module" required>
            @for (module of modules; track module) { <mat-option [value]="module">{{ module }}</mat-option> }
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Description</mat-label>
          <qms-rich-text formControlName="description" ariaLabel="Issue description" />
          <mat-error>Enter a description, up to 20,000 characters.</mat-error>
        </mat-form-field>
        <div class="context"><strong>Screen:</strong> {{ context.route }}</div>
        <input #fileInput type="file" multiple [accept]="acceptedTypes" hidden (change)="addFiles($event)" />
        <div class="attachment-heading">
          <button mat-stroked-button type="button" [disabled]="form.disabled" (click)="fileInput.click()">
            <mat-icon>attach_file</mat-icon>Attach files
          </button>
          <span>{{ files().length }}/10 files · 50 MB total maximum</span>
        </div>
        @for (file of files(); track $index) {
          <div class="attachment">
            <span>{{ file.name }}</span>
            <button mat-icon-button type="button" [disabled]="form.disabled" [attr.aria-label]="'Remove ' + file.name" (click)="removeFile($index)">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        }
        @if (error()) { <p role="alert" class="error">{{ error() }}</p> }
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" [disabled]="busy()" (click)="dialog.close()">Cancel</button>
      <button mat-flat-button type="submit" form="issue-report-form" [disabled]="busy()">
        <mat-icon>send</mat-icon>{{ busy() ? 'Sending...' : uncertain() ? 'Retry submission' : 'Send report' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: `
    .report-form { display: grid; gap: 12px; padding-top: 6px; }
    mat-form-field { width: 100%; }
    .context { overflow-wrap: anywhere; font-size: 13px; color: #525252; }
    .attachment-heading { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
    .attachment-heading span { font-size: 12px; color: #525252; }
    .attachment { display: flex; align-items: center; justify-content: space-between; gap: 8px; border-bottom: 1px solid #ddd; }
    .attachment span { min-width: 0; overflow-wrap: anywhere; }
    .error { color: #b3261e; margin: 0; }
    mat-dialog-actions { flex-wrap: wrap; }
  `,
})
export class IssueReportDialogComponent {
  readonly context = inject<IssueContext>(MAT_DIALOG_DATA);
  readonly dialog = inject(MatDialogRef<IssueReportDialogComponent>);
  private readonly service = inject(IssueReportService);
  readonly modules = ISSUE_MODULES;
  readonly acceptedTypes = [...FILE_TYPES].join(',');
  readonly files = signal<File[]>([]);
  readonly error = signal<string | null>(null);
  readonly busy = signal(false);
  readonly uncertain = signal(false);
  readonly form = new FormGroup({
    title: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(200)] }),
    description: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(20000)] }),
    module: new FormControl<IssueModule>(this.context.module, { nonNullable: true, validators: [Validators.required] }),
  });
  private readonly requestId = crypto.randomUUID();
  private pendingDraft: IssueDraft | null = null;

  addFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    const incoming = Array.from(input.files ?? []);
    input.value = '';
    if (this.form.disabled) return;
    const combined = [...this.files(), ...incoming];
    if (combined.length > 10 || combined.some(file => !FILE_TYPES.has(file.type) || file.size === 0)
      || combined.reduce((total, file) => total + file.size, 0) > 50 * 1024 * 1024) {
      this.error.set('Attach up to 10 non-empty images, Office documents, PDFs, TXT or CSV files, totaling at most 50 MB.');
      return;
    }
    this.error.set(null);
    this.files.set(combined);
  }

  removeFile(index: number): void {
    if (!this.form.disabled) this.files.update(files => files.filter((_, fileIndex) => fileIndex !== index));
  }

  submit(): void {
    if (this.busy()) return;
    if (!this.pendingDraft) {
      this.form.markAllAsTouched();
      const value = this.form.getRawValue();
      if (this.form.invalid || !value.title.trim() || !richTextToPlain(value.description).replace(/\u200b/g, '').trim()) {
        this.error.set('Enter a title and a description of the problem.');
        return;
      }
      this.pendingDraft = { ...this.context, ...value, requestId: this.requestId };
    }
    this.error.set(null);
    this.busy.set(true);
    this.form.disable();
    this.dialog.disableClose = true;
    this.service.submit(this.pendingDraft, this.files()).subscribe({
      next: receipt => {
        this.busy.set(false);
        this.dialog.disableClose = false;
        this.dialog.close(receipt);
      },
      error: (failure: { status?: number }) => {
        this.busy.set(false);
        this.dialog.disableClose = false;
        const uncertain = !failure.status || failure.status >= 500;
        this.uncertain.set(uncertain);
        if (!uncertain && failure.status !== 409) {
          this.pendingDraft = null;
          this.form.enable();
        }
        this.error.set(uncertain
          ? 'Submission could not be confirmed. Your report and files are preserved. Retry to confirm without creating a duplicate.'
          : failure.status === 409 ? 'The submission identifier was already used. Cancel and open a new report.'
            : 'The report was not accepted. Check the fields and files, or sign in again, then retry.');
      },
    });
  }
}